import _ from 'lodash';
import {connect} from 'react-redux';

import {getTimeFormat} from '../../../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {createSelector, makeMapStateToProps} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {parseApplicationUidFromTopicType} from '../../../../../../../helpers/models/topicHelpers';
import {TopicCommentUnfurlModel} from '../../../../../../../models/api/commentUnfurlModel';
import {AppState} from '../../../../../../../store/appState';
import {getFlatApplications} from '../../../../../../../store/data/applications/applicationsSelectors';
import {getCustomFields} from '../../../../../../../store/data/dataSelectors';
import {getDateTimeFormat} from '../../../../../../../store/data/localPreferences/localPreferencesSelectors';
import {getFindTeammate} from '../../../../../../../store/data/teammates/teammatesSelectors';
import {getTopics} from '../../../../../../../store/data/topics/topicsSelectors';
import {AnnotationTopicReferencePreviewerUnfurl} from './annotationTopicReferencePreviewerUnfurl';
import {CommentViewerTopicReferencePreviewerUnfurl} from './commentViewerTopicReferencePreviewerUnfurl';
import {ConversationSummaryTopicReferencePreviewerUnfurl} from './conversationSummaryTopicReferencePreviewerUnfurl';

/*
 * Props.
 */

interface CommentViewerReferencePreviewerUnfurlContainerProps {
  unfurl: TopicCommentUnfurlModel;
  isInteractionDisabled?: boolean;
}

/*
 * Selectors.
 */

function getTopicId(_state: AppState, props: CommentViewerReferencePreviewerUnfurlContainerProps) {
  return props.unfurl.topicId;
}

function getProvidedTopic(_state: AppState, props: CommentViewerReferencePreviewerUnfurlContainerProps) {
  return props.unfurl.topic;
}

function makeGetTopic() {
  return createSelector([getTopicId, getTopics, getProvidedTopic], (topicId, topics, topic) => {
    // If we have a topic passed in, we do not need to search for it.
    if (topic) {
      return topic;
    }
    if (!topicId) {
      return undefined;
    }
    return topics[topicId];
  });
}

function makeGetTopicUnfurlOwner(getTopic: ReturnType<typeof makeGetTopic>) {
  return createSelector([getFindTeammate, getTopic], (findTeammate, topic) => {
    if (!topic?.ownerId) {
      return undefined;
    }

    return findTeammate(topic.ownerId);
  });
}

function makeGetApplication(getTopic: ReturnType<typeof makeGetTopic>) {
  return createSelector([getTopic, getFlatApplications], (topic, applications) => {
    if (!topic) {
      return undefined;
    }

    const applicationUid = parseApplicationUidFromTopicType(topic);
    return _(applications).find((application) => application.uid === applicationUid);
  });
}

function makeGetTopicCustomFieldNameDataArray(getTopic: ReturnType<typeof makeGetTopic>) {
  return createSelector([getTopic, getCustomFields], (topic, customFieldMap) => {
    if (!topic) {
      return [];
    }
    const {customFieldAttributes} = topic;
    if (!customFieldAttributes) {
      return [];
    }

    const nameValueData = _(customFieldAttributes)
      .map(({id, value}) => {
        const customField = customFieldMap[id];
        if (!customField) {
          return undefined;
        }
        return {name: customField.name, value, id};
      })
      .compact()
      .value();

    return nameValueData;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CommentViewerReferencePreviewerUnfurlContainerProps>()(() => {
  const getTopic = makeGetTopic();
  return {
    topic: getTopic,
    owner: makeGetTopicUnfurlOwner(getTopic),
    application: makeGetApplication(getTopic),
    customFieldDataArray: makeGetTopicCustomFieldNameDataArray(getTopic),
    dateTimeFormat: getDateTimeFormat,
    timeFormat: getTimeFormat,
  };
});

// The topic unfurl previewer in conversation details.
export const ConversationSummaryTopicReferencePreviewerUnfurlContainer = connect(mapStateToProps)(
  ConversationSummaryTopicReferencePreviewerUnfurl,
);

// The topic unfurl previewer in comment viewer.
export const CommentViewerTopicReferencePreviewerUnfurlContainer = connect(mapStateToProps)(
  CommentViewerTopicReferencePreviewerUnfurl,
);

// The topic unfurl previewer for annotations and pills.
export const AnnotationTopicReferencePreviewerUnfurlContainer = connect(mapStateToProps)(
  AnnotationTopicReferencePreviewerUnfurl,
);
